import type { AssistantAction } from '../actions/assistant';
import type {
  Attachment,
  PageState,
  Reference,
  Skill,
  ToolCall,
} from '../types';

export type MessagePart =
  | { kind: 'text'; text: string; segment: number }
  | { kind: 'tool'; toolCall: ToolCall };

export interface AssistantMessage {
  uid: string;
  role: 'user' | 'assistant';
  status: 'running' | 'done' | 'cancelled';
  parts: MessagePart[];
  state?: PageState | null;
  meta?: Record<string, unknown> | null;
  [key: string]: unknown;
}

export interface Draft {
  text: string;
  attachments: Attachment[];
  references: Reference[];
  skills: Skill[];
}

export interface AssistantState {
  floatingOpen: boolean;
  permission: 'read' | 'write';
  pendingFocus: boolean;
  pendingRetry: unknown;
  tabIndex: number | null;
  followChanges: boolean;
  contextBlockId: string | null;
  draft: Draft;
  conversationId: string | null;
  pathname: string | null;
  running: boolean;
  messages: AssistantMessage[];
}

export const initialState: AssistantState = {
  floatingOpen: false,
  permission: 'read',
  pendingFocus: false,
  pendingRetry: null,
  tabIndex: null,
  followChanges: true,
  contextBlockId: null,
  draft: { text: '', attachments: [], references: [], skills: [] },
  conversationId: null,
  pathname: null,
  running: false,
  messages: [],
};

/** What a new conversation keeps of the old one: the chrome, not the transcript. */
const preserved = (state: AssistantState) => ({
  floatingOpen: state.floatingOpen,
  permission: state.permission,
  pendingFocus: state.pendingFocus,
  tabIndex: state.tabIndex,
  followChanges: state.followChanges,
  contextBlockId: state.contextBlockId,
  draft: state.draft,
});

const updateMessage = (
  messages: AssistantMessage[],
  uid: string,
  updater: (message: AssistantMessage) => AssistantMessage,
): AssistantMessage[] =>
  messages.map((message) => (message.uid === uid ? updater(message) : message));

export default function assistant(
  state: AssistantState = initialState,
  action: AssistantAction = { type: '@@INIT' } as AssistantAction,
): AssistantState {
  switch (action.type) {
    case 'ASSISTANT_SET_FLOATING_OPEN':
      return { ...state, floatingOpen: action.open };
    case 'ASSISTANT_SET_PERMISSION':
      return { ...state, permission: action.permission };
    case 'ASSISTANT_SET_PENDING_FOCUS':
      return { ...state, pendingFocus: action.pendingFocus };
    case 'ASSISTANT_SET_PENDING_RETRY':
      return { ...state, pendingRetry: action.pendingRetry };
    case 'ASSISTANT_SET_DRAFT':
      return { ...state, draft: { ...state.draft, ...action.draft } };
    case 'ASSISTANT_SET_TAB_INDEX':
      return { ...state, tabIndex: action.tabIndex };
    case 'ASSISTANT_SET_FOLLOW_CHANGES':
      return { ...state, followChanges: action.followChanges };
    case 'ASSISTANT_SET_CONTEXT_BLOCK':
      return { ...state, contextBlockId: action.contextBlockId };

    case 'ASSISTANT_LOAD_CONVERSATION':
      return {
        ...initialState,
        ...preserved(state),
        conversationId: action.conversationId,
        pathname: action.pathname,
        messages: action.messages,
      };

    case 'ASSISTANT_RESET_CONVERSATION':
      // Unsent input (the draft) survives a fresh conversation; a parked
      // retry belongs to the wiped transcript and does not.
      return {
        ...initialState,
        ...preserved(state),
        conversationId: action.conversationId,
        pathname: action.pathname,
      };

    case 'ASSISTANT_ADD_MESSAGE':
      return {
        ...state,
        messages: [...state.messages, action.message],
        running: action.message.status === 'running' ? true : state.running,
      };

    case 'ASSISTANT_APPEND_TOOL_CALL':
      return {
        ...state,
        messages: updateMessage(state.messages, action.uid, (message) => ({
          ...message,
          parts: [
            ...message.parts,
            { kind: 'tool', toolCall: action.toolCall },
          ],
        })),
      };

    case 'ASSISTANT_STREAM_MESSAGE':
      // Narration streams per segment: a chunk replaces the trailing text
      // part only when it belongs to the same segment — two consecutive
      // narration segments (nothing but a pause between them) stay separate
      // parts. Text between tool calls and the final reply are the same
      // thing: the model never knows whether another tool call follows.
      return {
        ...state,
        messages: updateMessage(state.messages, action.uid, (message) => {
          const parts = [...message.parts];
          const last = parts[parts.length - 1];
          const part: MessagePart = {
            kind: 'text',
            text: action.content,
            segment: action.segment,
          };
          if (last && last.kind === 'text' && last.segment === action.segment) {
            parts[parts.length - 1] = part;
          } else {
            parts.push(part);
          }

          return { ...message, parts };
        }),
      };

    case 'ASSISTANT_COMPLETE_MESSAGE':
      return {
        ...state,
        running: false,
        messages: updateMessage(state.messages, action.uid, (message) => ({
          ...message,
          status: 'done',
          state: action.state || null,
          meta: action.meta || null,
        })),
      };

    case 'ASSISTANT_CANCEL_MESSAGE':
      return {
        ...state,
        running: false,
        messages: updateMessage(state.messages, action.uid, (message) => ({
          ...message,
          status: 'cancelled',
        })),
      };

    default:
      return state;
  }
}
